/**
 * AI Auto Training Service
 * Automatically captures and updates AI knowledge when product data changes
 */

import aiChatService from './aiChatService';
import { 
  emitProductAdded,
  emitProductUpdated,
  emitProductDeleted,
  emitProductImageUpdated,
  emitProductStockUpdated,
  forceProductRefresh
} from '../utils/productEvents';

class AIAutoTrainingService {
  constructor() {
    this.isInitialized = false;
    this.lastTrainingTime = null;
    this.trainingQueue = [];
    this.isTraining = false;
    this.autoTrainingEnabled = true;
    this.trainingInterval = null;
    this.productWatchers = new Map();
    
    // Training statistics
    this.stats = {
      totalTrainingSessions: 0,
      lastAutoUpdate: null,
      productsProcessed: 0,
      categoriesProcessed: 0,
      errors: 0,
      successRate: 100
    };

    console.log('🤖 AI Auto Training Service initialized');
  }

  /**
   * Initialize auto training system
   */
  async initialize() {
    if (this.isInitialized) return;

    try {
      // Set up product event listeners
      this.setupProductEventListeners();
      
      // Set up periodic training checks
      this.setupPeriodicTraining();
      
      // Set up storage change listeners
      this.setupStorageListeners();
      
      this.isInitialized = true;
      console.log('✅ AI Auto Training Service initialized successfully');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize AI Auto Training Service:', error);
      return false;
    }
  }

  /**
   * Set up product event listeners
   */
  setupProductEventListeners() {
    // Listen for product events
    window.addEventListener('productAdded', (event) => {
      this.handleProductChange('added', event.detail);
    });

    window.addEventListener('productUpdated', (event) => {
      this.handleProductChange('updated', event.detail);
    });

    window.addEventListener('productDeleted', (event) => {
      this.handleProductChange('deleted', event.detail);
    });

    window.addEventListener('productImageUpdated', (event) => {
      this.handleProductChange('imageUpdated', event.detail);
    });

    window.addEventListener('productStockUpdated', (event) => {
      this.handleProductChange('stockUpdated', event.detail);
    });

    window.addEventListener('forceProductRefresh', () => {
      this.queueFullRetraining('Product refresh requested');
    });

    console.log('🎧 Product event listeners set up');
  }

  /**
   * Set up periodic training checks
   */
  setupPeriodicTraining() {
    // Check for updates every 30 seconds
    this.trainingInterval = setInterval(() => {
      this.checkForUpdates();
    }, 30000);

    console.log('⏰ Periodic training checks enabled (30s interval)');
  }

  /**
   * Set up storage change listeners
   */
  setupStorageListeners() {
    // Listen for localStorage changes (product data)
    window.addEventListener('storage', (event) => {
      if (event.key === 'gifted-solutions-products') {
        this.handleStorageChange(event);
      }
    });

    // Set up MutationObserver for DOM changes that might indicate product updates
    if (typeof window !== 'undefined' && window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.target.dataset?.productContainer) {
            this.queueIncrementalTraining('DOM product container changed');
          }
        });
      });

      // Observe product containers when they're available
      setTimeout(() => {
        const productContainers = document.querySelectorAll('[data-product-container]');
        productContainers.forEach(container => {
          observer.observe(container, { childList: true, subtree: true });
        });
      }, 1000);
    }

    console.log('💾 Storage change listeners set up');
  }

  /**
   * Handle product changes
   */
  async handleProductChange(changeType, productData) {
    console.log(`🔄 Product ${changeType}:`, productData?.name || productData?.id);
    
    const changeInfo = {
      type: changeType,
      product: productData,
      timestamp: new Date().toISOString(),
      processed: false
    };

    // Add to training queue
    this.trainingQueue.push(changeInfo);
    
    // Process queue if auto training is enabled
    if (this.autoTrainingEnabled) {
      await this.processTrainingQueue();
    }
  }

  /**
   * Handle storage changes
   */
  handleStorageChange(event) {
    console.log('💾 Product storage changed, queuing retraining');
    this.queueFullRetraining('Product storage updated');
  }

  /**
   * Queue full retraining
   */
  queueFullRetraining(reason = 'Manual request') {
    const trainingTask = {
      type: 'full_retrain',
      reason: reason,
      timestamp: new Date().toISOString(),
      processed: false
    };

    this.trainingQueue.push(trainingTask);
    
    if (this.autoTrainingEnabled) {
      this.processTrainingQueue();
    }
  }

  /**
   * Queue incremental training
   */
  queueIncrementalTraining(reason = 'Data change detected') {
    const trainingTask = {
      type: 'incremental_train',
      reason: reason,
      timestamp: new Date().toISOString(),
      processed: false
    };

    this.trainingQueue.push(trainingTask);
    
    if (this.autoTrainingEnabled) {
      this.processTrainingQueue();
    }
  }

  /**
   * Process training queue
   */
  async processTrainingQueue() {
    if (this.isTraining || this.trainingQueue.length === 0) {
      return;
    }

    this.isTraining = true;
    console.log(`🎯 Processing ${this.trainingQueue.length} training tasks`);

    try {
      while (this.trainingQueue.length > 0) {
        const task = this.trainingQueue.shift();
        await this.executeTrainingTask(task);
        task.processed = true;
      }

      this.stats.totalTrainingSessions++;
      this.stats.lastAutoUpdate = new Date().toISOString();
      this.lastTrainingTime = Date.now();
      
      console.log('✅ Training queue processed successfully');
      
      // Emit training completion event
      this.emitTrainingComplete();
      
    } catch (error) {
      console.error('❌ Error processing training queue:', error);
      this.stats.errors++;
      this.updateSuccessRate();
    } finally {
      this.isTraining = false;
    }
  }

  /**
   * Execute individual training task
   */
  async executeTrainingTask(task) {
    console.log(`🔧 Executing training task: ${task.type} - ${task.reason}`);

    try {
      switch (task.type) {
        case 'full_retrain':
          await this.performFullRetraining();
          break;
        case 'incremental_train':
          await this.performIncrementalTraining();
          break;
        case 'added':
        case 'updated':
        case 'deleted':
        case 'imageUpdated':
        case 'stockUpdated':
          await this.performIncrementalTraining();
          break;
        default:
          console.warn('Unknown training task type:', task.type);
      }
    } catch (error) {
      console.error(`❌ Failed to execute training task ${task.type}:`, error);
      throw error;
    }
  }

  /**
   * Perform full retraining
   */
  async performFullRetraining() {
    console.log('🔄 Performing full AI retraining...');

    try {
      // Get latest product data from localStorage
      const savedProducts = localStorage.getItem('gifted-solutions-products');
      let products = [];
      let categories = [];

      if (savedProducts) {
        products = JSON.parse(savedProducts);
        categories = this.extractCategories(products);
      }

      // Load data into AI service
      aiChatService.loadProductData(products, categories);
      
      this.stats.productsProcessed = products.length;
      this.stats.categoriesProcessed = categories.length;
      
      console.log(`✅ Full retraining complete: ${products.length} products, ${categories.length} categories`);
      
    } catch (error) {
      console.error('❌ Full retraining failed:', error);
      throw error;
    }
  }

  /**
   * Perform incremental training
   */
  async performIncrementalTraining() {
    console.log('🔄 Performing incremental AI training...');
    
    // For now, incremental training performs a full retrain
    // In the future, this could be optimized to only update specific products
    await this.performFullRetraining();
  }

  /**
   * Extract categories from products
   */
  extractCategories(products) {
    const categorySet = new Set();
    products.forEach(product => {
      if (product.category) {
        categorySet.add(product.category);
      }
    });
    
    return Array.from(categorySet).map(name => ({
      name,
      isActive: true,
      productCount: products.filter(p => p.category === name).length
    }));
  }

  /**
   * Check for updates periodically
   */
  async checkForUpdates() {
    if (!this.autoTrainingEnabled || this.isTraining) {
      return;
    }

    try {
      // Check if product data has changed since last training
      const savedProducts = localStorage.getItem('gifted-solutions-products');
      if (savedProducts) {
        const products = JSON.parse(savedProducts);
        const currentHash = this.generateDataHash(products);
        const lastHash = localStorage.getItem('ai-training-data-hash');
        
        if (currentHash !== lastHash) {
          console.log('📊 Product data changes detected, queuing retraining');
          this.queueFullRetraining('Periodic check detected changes');
          localStorage.setItem('ai-training-data-hash', currentHash);
        }
      }
    } catch (error) {
      console.warn('⚠️ Error during periodic update check:', error);
    }
  }

  /**
   * Generate hash for data change detection
   */
  generateDataHash(data) {
    const str = JSON.stringify(data);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  /**
   * Update success rate
   */
  updateSuccessRate() {
    const total = this.stats.totalTrainingSessions;
    const errors = this.stats.errors;
    this.stats.successRate = total > 0 ? ((total - errors) / total * 100) : 100;
  }

  /**
   * Emit training complete event
   */
  emitTrainingComplete() {
    const event = new CustomEvent('aiTrainingComplete', {
      detail: {
        timestamp: new Date().toISOString(),
        stats: this.stats,
        productsProcessed: this.stats.productsProcessed,
        categoriesProcessed: this.stats.categoriesProcessed
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Enable/disable auto training
   */
  setAutoTrainingEnabled(enabled) {
    this.autoTrainingEnabled = enabled;
    console.log(`🤖 Auto training ${enabled ? 'enabled' : 'disabled'}`);
    
    if (enabled && this.trainingQueue.length > 0) {
      this.processTrainingQueue();
    }
  }

  /**
   * Get training statistics
   */
  getStats() {
    return {
      ...this.stats,
      isTraining: this.isTraining,
      queueLength: this.trainingQueue.length,
      autoTrainingEnabled: this.autoTrainingEnabled,
      lastTrainingTime: this.lastTrainingTime
    };
  }

  /**
   * Manual trigger for full retraining
   */
  async triggerFullRetraining(reason = 'Manual trigger') {
    this.queueFullRetraining(reason);
    await this.processTrainingQueue();
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.trainingInterval) {
      clearInterval(this.trainingInterval);
    }
    
    // Remove event listeners
    window.removeEventListener('productAdded', this.handleProductChange);
    window.removeEventListener('productUpdated', this.handleProductChange);
    window.removeEventListener('productDeleted', this.handleProductChange);
    
    this.isInitialized = false;
    console.log('🧹 AI Auto Training Service destroyed');
  }
}

// Create singleton instance
const aiAutoTrainingService = new AIAutoTrainingService();

export default aiAutoTrainingService;
